import { useTranslation } from 'react-i18next';

import PathwayTextLogo from './red-pathway-text-logo.svg?react';

/** True when completion duration is present and non-negative (including zero). */
const hasCompletionDuration = (
  completionDurationSeconds?: number,
): completionDurationSeconds is number =>
  typeof completionDurationSeconds === 'number' &&
  completionDurationSeconds >= 0;

/** Formats seconds as compact duration for certificates (e.g. "1h 20m", "0m"). */
const formatCompactDuration = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '';
  }

  const totalMinutes = Math.ceil(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${minutes}m`;
};

interface CertificateFooterProps {
  planDescription?: string;
  completionDurationSeconds?: number;
  language?: 'en' | 'es' | (string & {});
  forPrint?: boolean;
}

/** Certificate footer shown for users with a permitted location in a regulated state. */
const CertificateFooter: React.FC<CertificateFooterProps> = ({
  planDescription,
  completionDurationSeconds,
  language,
  forPrint,
}) => {
  const { i18n } = useTranslation();
  const t = language ? i18n.getFixedT(language) : i18n.t;

  // Use fixed pixels for print mode, container query width units otherwise.
  const u = forPrint ? 'px' : 'cqw';
  // Scale factor: convert cqw to px (1cqw = 1% of container = 10.56px for 1056px width)
  const s = forPrint ? 10.56 : 1;
  const showCompletionDuration = hasCompletionDuration(
    completionDurationSeconds,
  );
  const compactDuration = showCompletionDuration
    ? formatCompactDuration(completionDurationSeconds)
    : '';
  const showFooter = showCompletionDuration || !!planDescription;
  const footerMinHeight = `${10.42 * s}${u}`;

  if (!showFooter) {
    return null;
  }

  return (
    <div
      className="mt-auto"
      style={{
        maxWidth: `${47.64 * s}${u}`,
        paddingTop: `${2 * s}${u}`,
      }}
    >
      {planDescription && (
        <div
          className="flex items-center justify-end text-black"
          style={{
            minHeight: showCompletionDuration ? undefined : footerMinHeight,
            marginBottom: showCompletionDuration ? `${0.5 * s}${u}` : 0,
          }}
        >
          <p
            className="text-right"
            style={{
              fontSize: `${1.3 * s}${u}`,
              lineHeight: `${2 * s}${u}`,
              fontWeight: 400,
            }}
            data-testid="CertificatePlanDescription"
          >
            {planDescription}
          </p>
        </div>
      )}
      {showCompletionDuration && (
        <div
          className="flex items-center justify-end gap-3 text-black"
          style={{
            minHeight: footerMinHeight,
            fontSize: `${1.5 * s}${u}`,
            lineHeight: `${2 * s}${u}`,
            fontWeight: 700,
          }}
          data-testid="CertificateCompletionDuration"
        >
          <span className="shrink-0">
            {t('certificates.completedIn', { duration: compactDuration })}
          </span>
          <PathwayTextLogo
            aria-hidden
            data-testid="PathwayLogo"
            className="shrink-0"
            style={{
              height: `${5 * s}${u}`,
              width: 'auto',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CertificateFooter;
